import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getDomEnvio, getEstado, getProvincia, setDireccion } from '../../actions/domiciliosEnvio'
import { getEstados } from '../../actions/estados'
import {
  setCodPostalDomEnvioMod,
  setDireccionDomEnvioMod,
  setEstadoDomEnvioMod,
  setLocalidadDomEnvioMod,
  setNombreDomEnvioMod,
  setProvinciaDomEnvioMod,
} from '../../actions/pedidos'
import { getProvincias } from '../../actions/provincias'
import { LONG_PEDIDO } from '../../helpers/longitudesPedido'
import i18n from '../../lang/idiomas'

const DomiciliosEnvio = props => {
  const dispatch = useDispatch()

  const domicilios_envio = useSelector(state => state.domiciliosEnvio.domicilios_envio)
  const domicilio = useSelector(state => state.domiciliosEnvio.domicilio)
  const estado = useSelector(state => state.domiciliosEnvio.estado)
  const provincia = useSelector(state => state.domiciliosEnvio.provincia)
  const pedido_cargando = useSelector(state => state.pedidos.isLoading)
  const provincias = useSelector(state => state.provincias.provincias)
  const estados = useSelector(state => state.estados.estados)
  const pedido = useSelector(state => state.pedidos.pedido)

  const [state, setState] = useState({
    domicilio: '',
    estado: '',
    provincia: '',
  })
  const [isCustomAddress, setIsCustomAddress] = useState(false)

  const customAddressButtonProps = useMemo(() => {
    return {
      className: !isCustomAddress ? 'btn btn-primary' : 'btn btn-danger',
      text: !isCustomAddress ? 'Nuevo domicilio de envío para el pedido' : 'Cancelar',
    }
  }, [isCustomAddress])
  // Caso 1824545: aqui habia un bloqueo de estado y provincia para Ceuta,
  // Melilla, Las Palmas y S.C. de Tenerife que nunca llego a aplicarse (la
  // comprobacion miraba un campo inexistente) y que, ademas, dejaba los dos
  // campos bloqueados mientras no hubiera un domicilio elegido. Se retira: el
  // tratamiento fiscal lo resuelve Libra al integrar el pedido.
  const editableFields = useMemo(
    () => props.editable && isCustomAddress,
    [isCustomAddress, props.editable],
  )

  useEffect(() => {
    dispatch(getDomEnvio())
    if (props.editable && estados.length === 0) {
      dispatch(getEstados())
    }
  }, [])

  // Caso 1825006: el servidor guarda cada dato del domicilio del pedido con
  // "nuevo or anterior" (pedidos_fn.domicilio_envio_mod), asi que enviar null
  // no borra una direccion ya guardada. Para volver a un domicilio de la
  // ficha se le envian sus propios datos. Un dato vacio de la ficha se envia
  // como null: '' es la marca de domicilio nuevo sin completar y bloquearia el
  // checkout.
  const aplicarDatosDomicilio = dom => {
    dispatch(setNombreDomEnvioMod(dom?.nombre || null))
    dispatch(setDireccionDomEnvioMod(dom?.direccion || null))
    dispatch(setLocalidadDomEnvioMod(dom?.localidad || null))
    dispatch(setEstadoDomEnvioMod(dom?.estado || null))
    dispatch(setProvinciaDomEnvioMod(dom?.provincia || null))
    dispatch(setCodPostalDomEnvioMod(dom?.cod_postal || null))
  }

  const camposDomicilioPedido = () => [
    [pedido.nombre_dom_envio, 'nombre'],
    [pedido.direccion_dom_envio, 'direccion'],
    [pedido.localidad_dom_envio, 'localidad'],
    [pedido.estado_dom_envio, 'estado'],
    [pedido.provincia_dom_envio, 'provincia'],
    [pedido.cod_postal_dom_envio, 'cod_postal'],
  ]

  // Al volver al resumen (p. ej. tras "Continuar comprando") el pedido puede
  // traer una direccion guardada. Si coincide con la del domicilio del pedido
  // es ese domicilio y se preselecciona; si difiere, se retoma en modo
  // domicilio nuevo con ese domicilio como base.
  const domicilioNuevoRevisado = useRef(false)
  useEffect(() => {
    if (pedido_cargando || props.pedidoGuardado || !props.editable) return
    if (!(domicilios_envio?.length > 0)) return

    const textos = [
      [pedido.nombre_dom_envio, setNombreDomEnvioMod],
      [pedido.direccion_dom_envio, setDireccionDomEnvioMod],
      [pedido.localidad_dom_envio, setLocalidadDomEnvioMod],
      [pedido.cod_postal_dom_envio, setCodPostalDomEnvioMod],
    ]

    const estadoProvincia = [
      [pedido.estado_dom_envio, setEstadoDomEnvioMod],
      [pedido.provincia_dom_envio, setProvinciaDomEnvioMod],
    ]

    if (isCustomAddress) {
      // Si el pedido se recarga desde el servidor, lo que no se guardo llega
      // a null: se deja vacio para que el checkout obligue a completarlo y no
      // se muestre el dato del domicilio base.
      ;[...textos, ...estadoProvincia].forEach(([valor, accion]) => {
        if (valor === null || valor === undefined) dispatch(accion(''))
      })
      return
    }

    if (domicilioNuevoRevisado.current) return
    domicilioNuevoRevisado.current = true

    const hayTextoEscrito = textos.some(([valor]) => typeof valor === 'string' && valor !== '')

    if (!hayTextoEscrito) {
      // Domicilio nuevo empezado y sin escribir nada: se descarta.
      if (textos.some(([valor]) => valor === '')) {
        textos.forEach(([, accion]) => dispatch(accion(null)))
        dispatch(setEstadoDomEnvioMod(null))
        dispatch(setProvinciaDomEnvioMod(null))
      }
      return
    }

    const base = domicilios_envio.find(
      dom => dom.numero_direccion === parseInt(pedido.domicilio_envio),
    )
    if (base) {
      dispatch(getEstado(base.estado))
      dispatch(getProvincia(base.estado, base.provincia))
      dispatch(setDireccion(base))
      setState(prev => ({ ...prev, domicilio: base }))

      const difiereDeBase = camposDomicilioPedido().some(
        ([valor, campo]) =>
          valor !== null && valor !== undefined && valor !== '' && valor !== (base[campo] ?? ''),
      )
      if (!difiereDeBase) {
        // Es la direccion del propio domicilio (p. ej. tras "Cancelar"): se
        // muestra ese domicilio, sin modo domicilio nuevo.
        dispatch(getProvincias(base.estado))
        return
      }
    }

    // Lo no guardado se deja en blanco, tambien estado y provincia: no se
    // heredan del domicilio base.
    ;[...textos, ...estadoProvincia].forEach(([valor, accion]) => {
      if (valor === null || valor === undefined) dispatch(accion(''))
    })
    if (pedido.estado_dom_envio) dispatch(getProvincias(pedido.estado_dom_envio))

    setIsCustomAddress(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido_cargando, domicilios_envio, pedido.id_pedido, props.pedidoGuardado])

  useEffect(() => {
    if (!pedido_cargando) {
      if (
        props.pedidoGuardado &&
        domicilios_envio?.length > 0 &&
        (pedido.domicilio_envio || pedido.domicilio_envio === 0) &&
        domicilio?.numero_direccion !== pedido.domicilio_envio
      ) {
        const dom_env = domicilios_envio.filter(
          domicilio => domicilio.numero_direccion === parseInt(pedido.domicilio_envio),
        )

        if (dom_env.length === 0) {
          dispatch(getEstado(domicilios_envio[0].estado))
          dispatch(getProvincias(domicilios_envio[0].estado))
          dispatch(getProvincia(domicilios_envio[0].estado, domicilios_envio[0].provincia))
          dispatch(setDireccion(domicilios_envio[0]))
          setState({
            ...state,
            domicilio: domicilios_envio[0],
            estado: domicilios_envio[0].estado,
            provincia: domicilios_envio[0].provincia,
            nombre: domicilios_envio[0].nombre,
            direccion: domicilios_envio[0].direccion,
            localidad: domicilios_envio[0].localidad,
            cod_postal: domicilios_envio[0].cod_postal,
          })
          return
        }

        dispatch(getEstado(dom_env[0].estado))
        dispatch(getProvincias(dom_env[0].estado))
        dispatch(getProvincia(dom_env[0].estado, dom_env[0].provincia))
        dispatch(setDireccion(dom_env[0]))
      }
    }
  }, [pedido_cargando, domicilios_envio, props.pedidoGuardado])

  const createCustomAddress = () => {
    if (!isCustomAddress && !domicilio?.id) return
    if (isCustomAddress) {
      // Al cancelar se vuelve al domicilio base elegido y se le envian sus
      // datos, para que sustituyan en el servidor a la direccion que hubiera
      // guardada en el pedido.
      const base = domicilio?.id ? domicilio : (domicilios_envio?.[0] ?? {})
      dispatch(setDireccion(base))
      if (base?.estado) dispatch(getProvincias(base.estado))
      aplicarDatosDomicilio(base)
      setIsCustomAddress(false)
      return
    }
    // Caso 1825006: los datos de entrega se escriben desde cero, estado y
    // provincia incluidos; del domicilio base no se toma nada.
    dispatch(setNombreDomEnvioMod(''))
    dispatch(setDireccionDomEnvioMod(''))
    dispatch(setLocalidadDomEnvioMod(''))
    dispatch(setCodPostalDomEnvioMod(''))
    dispatch(setEstadoDomEnvioMod(''))
    dispatch(setProvinciaDomEnvioMod(''))
    setIsCustomAddress(true)
  }

  const onChange = e => {
    e.preventDefault()
    if (e.target.name === 'domicilio' && e.target.value !== '') {
      const direccion = domicilios_envio.filter(dom => dom.id === parseInt(e.target.value))

      setState({ ...state, domicilio: direccion[0] })

      if (isCustomAddress) {
        // Con un domicilio nuevo en curso el selector solo cambia el domicilio
        // base: se conservan los datos escritos y la lista de provincias del
        // estado elegido en el formulario.
        dispatch(getEstado(direccion[0].estado))
        dispatch(getProvincia(direccion[0].estado, direccion[0].provincia))
        dispatch(setDireccion(direccion[0]))
        return
      }

      dispatch(getEstado(direccion[0].estado))
      dispatch(getProvincias(direccion[0].estado))
      dispatch(getProvincia(direccion[0].estado, direccion[0].provincia))
      dispatch(setDireccion(direccion[0]))
      if (camposDomicilioPedido().some(([valor]) => valor !== null && valor !== undefined)) {
        // El pedido ya lleva una direccion (p. ej. la de otro domicilio tras
        // "Cancelar"): se sustituye por la del domicilio elegido.
        aplicarDatosDomicilio(direccion[0])
      } else {
        aplicarDatosDomicilio(null)
      }
    } else {
      if (e.target.name === 'estado') {
        dispatch(getProvincias(e.target.value))
        dispatch(setEstadoDomEnvioMod(e.target.value))
        // La provincia que hubiera puesta ya no pertenece al nuevo estado.
        dispatch(setProvinciaDomEnvioMod(''))
      } else if (e.target.name === 'provincia') {
        dispatch(setProvinciaDomEnvioMod(e.target.value))
      } else {
        setState({
          ...state,
          [e.target.name]: e.target.value,
        })
      }
    }
  }

  const formatearDomicilio = dom => {
    const direccion = dom.direccion?.toUpperCase()
    const localidad = [dom.cod_postal, dom.localidad?.toUpperCase()].filter(Boolean).join(' ')
    return [direccion, localidad].filter(Boolean).join(' — ') || dom.nombre
  }

  return (
    <Fragment>
      <div className='tt-shopcart-box domicilio-envio-box'>
        <h4 className='tt-title'>{i18n.t('domicilios.direnvio')}</h4>
        <div className='address-title'>
          <p>{i18n.t('domicilios.seldiren')}</p>
          {props?.editable && !props?.pedidoGuardado ? (
            <button
              onClick={createCustomAddress}
              type='button'
              className={customAddressButtonProps.className}
              // Sin domicilio elegido no se puede crear uno nuevo; "Cancelar" siempre se permite
              disabled={!isCustomAddress && !domicilio?.id}
              title={!isCustomAddress && !domicilio?.id ? i18n.t('domicilios.seldoenv') : ''}>
              {customAddressButtonProps.text}
            </button>
          ) : (
            ''
          )}
        </div>
        <form className='form-default'>
          <div className='row'>
            <div className=' col-sm-12 col-md-12 col-lg-12'>
              <div className='form-group '>
                <label htmlFor='input_domicilio'>
                  {isCustomAddress
                    ? i18n.t('domicilios.dombase', 'DOMICILIO BASE')
                    : i18n.t('domicilios.domicil')}{' '}
                  <sup>*</sup>
                </label>
                <select
                  className='form-control'
                  id='input_domicilio'
                  name='domicilio'
                  onChange={onChange}
                  disabled={!!(pedido_cargando || props.pedidoGuardado)}
                  value={domicilio.id || ''}>
                  <option
                    key='0'
                    value=''
                    disabled>
                    {i18n.t('domicilios.seldoenv')}
                  </option>
                  {domicilios_envio.map(dom => {
                    return (
                      <option
                        key={dom.id}
                        value={dom.id}>
                        {formatearDomicilio(dom)}
                      </option>
                    )
                  })}
                </select>
                {isCustomAddress ? (
                  <small className='form-text text-muted'>
                    {i18n.t(
                      'domicilios.dombasea',
                      'Se usa como referencia para los portes. La entrega se hará en la dirección indicada abajo.',
                    )}
                  </small>
                ) : (
                  ''
                )}
              </div>
            </div>
          </div>

          <div className='row'>
            <div className='col-sm-12 col-md-6 col-lg-6'>
              <div className='form-group'>
                <label htmlFor='input_nombre'>{i18n.t('domicilios.nombre')}</label>

                <input
                  type='text'
                  id='input_nombre'
                  name='nombre'
                  maxLength={LONG_PEDIDO.nombre_dom_envio}
                  value={
                    isCustomAddress && pedido.nombre_dom_envio === ''
                      ? ''
                      : pedido.nombre_dom_envio || domicilio.nombre || ''
                  }
                  className='form-control'
                  placeholder='Nombre'
                  onChange={e => {
                    dispatch(setNombreDomEnvioMod(e.target.value))
                  }}
                  readOnly={!editableFields}
                  disabled={pedido_cargando || props.pedidoGuardado || !editableFields}
                />
              </div>
            </div>
            <div className='col-sm-12 col-md-6 col-lg-6'>
              <div className='form-group'>
                <label htmlFor='input_direccion'>{i18n.t('domicilios.direc')}</label>
                <input
                  type='text'
                  id='input_direccion'
                  maxLength={LONG_PEDIDO.direccion_dom_envio}
                  name='direccion'
                  value={
                    isCustomAddress && pedido.direccion_dom_envio === ''
                      ? ''
                      : pedido.direccion_dom_envio || domicilio.direccion || ''
                  }
                  className='form-control'
                  placeholder='Dirección'
                  onChange={e => {
                    dispatch(setDireccionDomEnvioMod(e.target.value))
                  }}
                  readOnly={!editableFields}
                  disabled={pedido_cargando || props.pedidoGuardado || !editableFields}
                />
              </div>
            </div>
          </div>
          <div className='row'>
            <div className='col-sm-12 col-md-6 col-lg-6'>
              <div className='form-group'>
                <label htmlFor='input_estado'>
                  {i18n.t('domicilios.estdomay')} <sup>*</sup>
                </label>

                {editableFields ? (
                  <select
                    className='form-control'
                    name='estado'
                    id='input_estado'
                    onChange={onChange}
                    value={
                      isCustomAddress && pedido.estado_dom_envio === ''
                        ? ''
                        : pedido.estado_dom_envio || domicilio.estado || ''
                    }
                    placeholder='Estado'
                    disabled={pedido_cargando || props.pedidoGuardado}>
                    <option
                      value=''
                      disabled>
                      {i18n.t('domicilios.estdomin')}
                    </option>
                    {estados?.length > 0
                      ? estados.map((estado, index) => {
                          return (
                            <option
                              key={index}
                              value={estado.codigo}>
                              {estado.nombre}
                            </option>
                          )
                        })
                      : ''}
                  </select>
                ) : (
                  <input
                    type='text'
                    id='input_estado'
                    name='estado'
                    value={estado.nombre || ''}
                    className='form-control'
                    placeholder={i18n.t('domicilios.estdomin')}
                    onChange={onChange}
                    readOnly
                    disabled
                  />
                )}
              </div>
            </div>
            <div className='col-sm-12 col-md-6 col-lg-6'>
              <div className='form-group'>
                <label htmlFor='input_provincia'>
                  {i18n.t('domicilios.prov')} <sup>*</sup>
                </label>
                {editableFields ? (
                  <select
                    className='form-control'
                    name='provincia'
                    id='input_provincia'
                    onChange={onChange}
                    value={
                      isCustomAddress && pedido.provincia_dom_envio === ''
                        ? ''
                        : pedido.provincia_dom_envio || domicilio.provincia || ''
                    }
                    placeholder='Provincia'
                    // Sin estado la lista de provincias puede ser la de otro estado
                    disabled={pedido_cargando || props.pedidoGuardado || !pedido.estado_dom_envio}>
                    <option
                      value=''
                      disabled>
                      {i18n.t('domicilios.provmin')}
                    </option>
                    {provincias && provincias.length > 0
                      ? provincias.map((provincia, index) => {
                          return (
                            <option
                              key={index}
                              value={provincia.provincia}>
                              {provincia.nombre}
                            </option>
                          )
                        })
                      : ''}
                  </select>
                ) : (
                  <input
                    type='text'
                    name='provincia'
                    id='input_provincia'
                    value={provincia?.nombre || ''}
                    className='form-control'
                    placeholder={i18n.t('domicilios.provmin')}
                    onChange={onChange}
                    readOnly
                    disabled
                  />
                )}
              </div>
            </div>
          </div>
          <div className='row'>
            <div className='col-sm-12 col-md-4 col-lg-4'>
              <div className='form-group'>
                <label htmlFor='input_localidad'>{i18n.t('domicilios.local')}</label>
                <input
                  type='text'
                  name='localidad'
                  id='input_localidad'
                  value={
                    isCustomAddress && pedido.localidad_dom_envio === ''
                      ? ''
                      : pedido.localidad_dom_envio || domicilio.localidad || ''
                  }
                  className='form-control'
                  maxLength={LONG_PEDIDO.localidad_dom_envio}
                  placeholder={i18n.t('domicilios.localmin')}
                  onChange={e => {
                    dispatch(setLocalidadDomEnvioMod(e.target.value))
                  }}
                  readOnly={!editableFields}
                  disabled={pedido_cargando || props.pedidoGuardado || !editableFields}
                />
              </div>
            </div>
            <div className='col-sm-12 col-md-4 col-lg-4'>
              <div className='form-group'>
                <label htmlFor='input_cod_postal'>{i18n.t('domicilios.codpstal')}</label>
                <input
                  type='text'
                  id='input_cod_postal'
                  name='cod_postal'
                  value={
                    isCustomAddress && pedido.cod_postal_dom_envio === ''
                      ? ''
                      : pedido.cod_postal_dom_envio || domicilio.cod_postal || ''
                  }
                  className='form-control'
                  placeholder={i18n.t('domicilios.codposmi')}
                  maxLength={LONG_PEDIDO.cod_postal_dom_envio}
                  onChange={e => {
                    dispatch(setCodPostalDomEnvioMod(e.target.value))
                  }}
                  readOnly={!editableFields}
                  disabled={pedido_cargando || props.pedidoGuardado || !editableFields}
                />
              </div>
            </div>
            {/**
            <div className='col-sm-12 col-md-4 col-lg-4'>
                      <div className='form-group tipo-portes-group'>
                <label htmlFor='input_tipo_portes'>{i18n.t('domicilios.tipport')}</label>
                <input
                  type='checkbox'
                  id='input_tipo_portes'
                  name='tipo_portes'
                  className='form-control'
                  value={Boolean(pedido?.tipo_portes_dom_envio === 'S')}
                  placeholder={i18n.t('domicilios.tippormi')}
                  onChange={e => {
                    if (e.target.checked) {
                      dispatch(setTipoPortesDomEnvioMod('S'))
                    } else {
                      dispatch(setTipoPortesDomEnvioMod('N'))
                    }
                  }}
                  readOnly={!props.editable}
                  disabled={pedido_cargando || props.pedidoGuardado}
                  checked={pedido?.tipo_portes_dom_envio === 'S'}
                />

              </div>
            </div>
      **/}
          </div>
        </form>
      </div>
    </Fragment>
  )
}

export default DomiciliosEnvio
